import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Recycle, Truck, Check, Repeat } from "lucide-react";
import { formatJMD } from "@/lib/format";
import { BACKEND_URL } from "@/lib/api";

const FIVE_GAL_IMG = "/brand/carboys.jpg";

export const RefillSection = ({ products, onSelect }) => {
  const refills = products.filter((p) => p.type === "refill");

  return (
    <section id="refills" data-testid="section-refills-info" className="relative py-24 bg-slate-50 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-600">Refill & Bottle Swap</span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 mt-3">
            Sustainable hydration, <span className="cw-text-gradient">on repeat.</span>
          </h2>
          <p className="mt-4 text-slate-600 text-base lg:text-lg leading-relaxed">
            Cost-effective, eco-friendly refills for high-volume Jamaican homes and corporate offices. 5&nbsp;gallon
            station swaps and  carboy doorstep exchange.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-12 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative rounded-3xl overflow-hidden min-h-[320px] group"
          >
            <img src={FIVE_GAL_IMG} alt="Cool Waves 5 gallon water carboys" className="absolute inset-0 w-full h-full object-cover object-[center_70%] group-hover:scale-105 transition-transform duration-700" data-testid="refill-hero-img" />
            <div className="absolute inset-0 bg-gradient-to-t from-sky-950 via-sky-950/40 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8">
              <div className="w-12 h-12 rounded-2xl bg-sky-400/90 flex items-center justify-center mb-4">
                <Truck className="w-6 h-6 text-sky-950" />
              </div>
              <h3 className="font-display text-2xl font-bold text-white">5 Gallon Carboy Swap</h3>
              <p className="text-sky-100/85 mt-2 text-sm leading-relaxed max-w-md">
                Effortless exchange at your door. Hand us the empties, our drivers deliver fresh sealed jugs across
                Kingston & St. Andrew.
              </p>
            </div>
          </motion.div>

          <div className="grid gap-6">
            {refills.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-3xl p-7 border border-slate-200 shadow-sm hover:shadow-xl hover:border-sky-200 transition-all flex flex-col"
              >
                <div className="flex items-start justify-between">
                  <img
                    src={r.image_url ? `${BACKEND_URL}${r.image_url}` : r.size.includes("5 Gallon") ? "/brand/carboys-crop.jpg" : "/brand/bottle-crop.png"}
                    alt={r.name}
                    className="w-20 h-20 rounded-2xl object-cover shadow-md"
                    data-testid={`refill-image-${r.id}`}
                  />
                  <div className="text-right">
                    <div className="font-mono-cw text-2xl font-extrabold text-sky-950">{formatJMD(r.price_jmd)}</div>
                    <div className="text-xs text-slate-400 font-semibold">per unit</div>
                  </div>
                </div>
                <h3 className="font-display text-xl font-bold text-slate-900 mt-4">{r.name}</h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed flex-1">{r.description}</p>
                <button
                  data-testid={`refill-select-${r.id}`}
                  onClick={() => onSelect(r)}
                  className="mt-5 inline-flex items-center justify-center gap-2 h-11 rounded-full bg-sky-600 hover:bg-sky-500 text-white font-semibold text-sm transition-colors"
                >
                  <Check className="w-4 h-4" /> Select this refill
                </button>
                <Link to={`/subscribe?product=${r.id}`} data-testid={`refill-subscribe-${r.id}`} className="mt-2 inline-flex items-center justify-center gap-2 h-10 rounded-full border border-sky-200 text-sky-700 hover:bg-sky-50 font-semibold text-sm transition-colors">
                  <Repeat className="w-4 h-4" /> Make it a weekly standing order
                </Link>
              </motion.div>
            ))}
            {refills.length === 0 && (
              <div className="bg-white rounded-3xl p-7 border border-dashed border-slate-300 text-slate-400 text-center">
                Refill options coming soon.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
